import { EventEmitter } from "events";

// Singleton wrapper for a WebSocket connection
class WebSocketService extends EventEmitter {
    outbox = [];
    static instance = null;

    static getInstance() {
        if (!WebSocketService.instance) {
            WebSocketService.instance = new WebSocketService();
        }
        return WebSocketService.instance;
    }

    constructor() {
        super();
        // If a singleton instance already exists, return it
        if (WebSocketService.instance) {
            return WebSocketService.instance;
        }

        this.ws = null;
        this.reconnectAttempts = 0;
        this.maxReconnectDelay = 30000; // 30 seconds
        this.heartbeatInterval = null;
        this.heartbeatMonitorInterval = null;
        this.heartbeatIntervalTime = 30000; // 30 seconds
        // Initially do not attempt to reconnect until explicitly started
        this.shouldReconnect = false;
        this.isReconnecting = false; // Flag to prevent overlapping reconnect attempts
        this.lastPongTimestamp = Date.now();

        // Prepare connection monitoring but do not start until a connection is requested
        this.connectionMonitorInterval = null;

        // Listen for the online event to trigger reconnection
        window.addEventListener("online", () => {
            if (
                !this.ws ||
                (this.ws.readyState !== WebSocket.OPEN &&
                    this.ws.readyState !== WebSocket.CONNECTING)
            ) {
                this.handleReconnect();
            }
        });

        // Listen for visibility change to trigger reconnection when the tab becomes active
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") {
                if (
                    !this.ws ||
                    (this.ws.readyState !== WebSocket.OPEN &&
                        this.ws.readyState !== WebSocket.CONNECTING)
                ) {
                    this.handleReconnect();
                }
            }
        });

        // Bind methods
        this.handleReconnect = this.handleReconnect.bind(this);
        this.connectWebSocket = this.connectWebSocket.bind(this);

        WebSocketService.instance = this;
    }

    initializeCookieAndConnect() {
        if (!this.shouldReconnect) return; // Guard against reconnecting after logout

        const url = `${process.env.REACT_APP_API_URL}/auth/set-cookie`;
        /* console.log("[WS_COOKIE] start", {
            url,
            credentials: "include",
            cookieEnabled: navigator.cookieEnabled,
            beforeCookie: document.cookie,
            env: process.env.NODE_ENV,
        }); */

        fetch(url, { method: "GET", credentials: "include" })
            .then((res) => {
                /* console.log("[WS_COOKIE] done", {
                    status: res.status,
                    ok: res.ok,
                    afterCookie: document.cookie,
                }); */
                this.connectWebSocket();
            })
            .catch((error) => {
                // console.error("[WS_COOKIE] error", error);
                if (!navigator.onLine) {
                    // console.warn("Offline: will not attempt reconnection until online");
                } else {
                    setTimeout(() => {
                        this.handleReconnect();
                    }, 3000);
                }
            });
    }

    startConnectionMonitor() {
        if (this.connectionMonitorInterval) return;
        this.connectionMonitorInterval = setInterval(() => {
            if (
                this.shouldReconnect &&
                (!this.ws ||
                    (this.ws.readyState !== WebSocket.OPEN &&
                        this.ws.readyState !== WebSocket.CONNECTING))
            ) {
                if (!this.isReconnecting) {
                    // console.log("Connection inactive, attempting automatic reconnect");
                    this.handleReconnect();
                }
            }
        }, 5000);

        this.heartbeatMonitorInterval = setInterval(() => {
            if (
                this.ws &&
                this.ws.readyState === WebSocket.OPEN &&
                Date.now() - this.lastPongTimestamp > this.heartbeatIntervalTime * 2
            ) {
                console.warn("No pong received, reconnecting...");
                this.handleReconnect();
            }
        }, 5000);
    }

    connectWebSocket() {
        this.shouldReconnect = true;
        this.startConnectionMonitor();

        if (this.ws) {
            if (
                this.ws.readyState === WebSocket.OPEN ||
                this.ws.readyState === WebSocket.CONNECTING
            ) {
                return;
            }
            this.ws = null;
        }

        let wsUrl = process.env.REACT_APP_WS_URL;
        const usingCapacitorToken = !!(window.Capacitor && window.capacitorToken);
        if (usingCapacitorToken) {
            try {
                const urlObj = new URL(wsUrl);
                urlObj.searchParams.set("token", window.capacitorToken);
                wsUrl = urlObj.toString();
            } catch (e) {
                console.error("Error appending token to ws URL", e);
            }
        }

        /* console.log("[WS_CONNECT] open", {
            wsUrl,
            usingCapacitorToken,
            readyState: this.ws?.readyState ?? "NONE",
        }); */

        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
            this.reconnectAttempts = 0;
            this.isReconnecting = false;
            this.lastPongTimestamp = Date.now();
            this.startHeartbeat();
            this.emit("open");
            // console.log("[WS_CONNECT] connected");
            this.flushOutbox();
        };

        this.ws.onerror = (error) => {
            // console.error("[WS_CONNECT] error", error);
            this.emit("error", error);
        };

        this.ws.onclose = (event) => {
            this.stopHeartbeat();
            this.ws = null;
            /* console.warn("[WS_CONNECT] close", {
                code: event?.code,
                reason: event?.reason,
                wasClean: event?.wasClean,
            }); */
            if (this.shouldReconnect) {
                this.handleReconnect();
            }
            this.emit("close", event);
        };

        this.ws.onmessage = (event) => {
            if (!event.data) return;
            try {
                const messageObj = JSON.parse(event.data);
                if (messageObj.type === "pong") {
                    this.lastPongTimestamp = Date.now();
                    return;
                }
                this.emit("message", messageObj);
                if (messageObj.target) this.emit("message:target", messageObj);
            } catch (error) {
                console.error("Error parsing message:", error);
            }
        };
    }

    handleReconnect() {
        if (!this.shouldReconnect) return;
        // Do not attempt reconnect if offline
        if (!navigator.onLine) {
            console.warn("Offline: postponing reconnect");
            return;
        }
        // If there is already an active or pending connection, do nothing.
        if (
            this.ws &&
            (this.ws.readyState === WebSocket.OPEN ||
                this.ws.readyState === WebSocket.CONNECTING)
        ) {
            return;
        }
        if (this.isReconnecting) return; // Avoid duplicate reconnects

        this.isReconnecting = true;
        this.emit("reconnecting");
        this.reconnectAttempts += 1;
        const backoffDelay = Math.min(
            1000 * 2 ** this.reconnectAttempts,
            this.maxReconnectDelay
        );
        console.log(`Attempting to reconnect in ${backoffDelay / 1000} seconds`);

        setTimeout(() => {
            // Double-check before reconnecting.
            if (
                this.shouldReconnect &&
                (!this.ws ||
                    (this.ws.readyState !== WebSocket.OPEN &&
                        this.ws.readyState !== WebSocket.CONNECTING))
            ) {
                this.initializeCookieAndConnect();
            }
            this.isReconnecting = false;
        }, backoffDelay);
    }

    startHeartbeat() {
        this.stopHeartbeat();
        this.heartbeatInterval = setInterval(() => {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                const pingMessage = JSON.stringify({ type: "ping" });
                this.ws.send(pingMessage);
            }
        }, this.heartbeatIntervalTime);
    }

    stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    sendMessage(message) {
        const canSend = this.ws && this.ws.readyState === WebSocket.OPEN;
        if (canSend) {
            this.ws.send(JSON.stringify(message));
            // console.debug("[WS_SEND]", { type: message?.type, when: "OPEN" });
        } else {
            this.outbox.push(message);
            /* console.warn("[WS_BUFFER] queued (not OPEN)", {
                type: message?.type,
                qlen: this.outbox.length,
                rs: this.ws?.readyState,
            }); */
        }
    }

    // helper
    flushOutbox() {
        if (!(this.ws && this.ws.readyState === WebSocket.OPEN)) return;
        while (this.outbox.length) {
            const m = this.outbox.shift();
            this.ws.send(JSON.stringify(m));
            // console.debug("[WS_FLUSH_SEND]", { type: m?.type });
        }
    }

    // Intentionally close the websocket and prevent reconnection
    closeWebSocket() {
        this.shouldReconnect = false;
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.stopHeartbeat();
        if (this.connectionMonitorInterval) {
            clearInterval(this.connectionMonitorInterval);
            this.connectionMonitorInterval = null;
        }
        if (this.heartbeatMonitorInterval) {
            clearInterval(this.heartbeatMonitorInterval);
            this.heartbeatMonitorInterval = null;
        }
    }
}

export default WebSocketService.getInstance();
